import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AddressAutocomplete from '../components/AddService/AddressAutocomplete';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { vendorLocationsAdminService } from '../services/vendorLocationsAdmin.service';

const DEFAULT_HOURS = {
  monday: { open: '06:00', close: '18:00' },
  tuesday: { open: '06:00', close: '18:00' },
  wednesday: { open: '06:00', close: '18:00' },
  thursday: { open: '06:00', close: '18:00' },
  friday: { open: '06:00', close: '18:00' },
  saturday: { open: '07:00', close: '16:00' },
  sunday: { open: '08:00', close: '14:00' },
};

export default function VendorLocationsAdmin() {
  const [vendors, setVendors] = useState([]);
  const [selectedVendorIds, setSelectedVendorIds] = useState([]);
  const [vendorSearch, setVendorSearch] = useState('');
  const [vendorDropdownOpen, setVendorDropdownOpen] = useState(false);

  const [locationsByVendor, setLocationsByVendor] = useState([]); // [{ vendor, locations, total }]
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [error, setError] = useState('');

  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const vendorDropdownRef = useRef(null);

  const vendorOptions = useMemo(() => {
    return vendors.map((row) => ({
      id: row?.vendor?.id,
      label: `${row?.vendor?.vendor_name || 'Vendor'} (${row?.vendor?.vendor_code || '-'})`,
      raw: row,
    }));
  }, [vendors]);

  const fetchVendors = useCallback(async (search) => {
    setLoadingVendors(true);
    setError('');
    try {
      const res = await vendorLocationsAdminService.listVendors({
        page: 1,
        limit: 100,
        search: (search || '').trim() || '',
        is_active: true,
      });
      setVendors(Array.isArray(res?.items) ? res.items : []);
    } catch (e) {
      setError(e?.message || 'Failed to load vendors');
      setVendors([]);
    } finally {
      setLoadingVendors(false);
    }
  }, []);

  const fetchLocations = useCallback(async () => {
    if (!selectedVendorIds || selectedVendorIds.length === 0) return;
    setLoadingLocations(true);
    setError('');
    try {
      const results = await Promise.all(
        selectedVendorIds.map((vid) =>
          vendorLocationsAdminService.getVendorLocations(Number(vid), {}),
        ),
      );
      const normalized = results
        .map((r) => ({
          vendor: r?.vendor || null,
          locations: Array.isArray(r?.locations) ? r.locations : [],
          total: Number(r?.total ?? (Array.isArray(r?.locations) ? r.locations.length : 0)),
        }))
        .filter((x) => x.vendor && x.vendor.id);

      setLocationsByVendor(normalized);
    } catch (e) {
      setError(e?.message || 'Failed to load locations');
      setLocationsByVendor([]);
    } finally {
      setLoadingLocations(false);
    }
  }, [selectedVendorIds]);

  useEffect(() => {
    fetchVendors('');
  }, [fetchVendors]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!vendorDropdownOpen) return;
      const el = vendorDropdownRef.current;
      if (el && !el.contains(e.target)) setVendorDropdownOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [vendorDropdownOpen]);

  const filteredVendors = useMemo(() => {
    const q = vendorSearch.trim().toLowerCase();
    if (!q) return vendorOptions;
    return vendorOptions.filter((v) => v.label.toLowerCase().includes(q));
  }, [vendorOptions, vendorSearch]);

  const selectedVendorSummaries = useMemo(() => {
    const map = new Map(vendorOptions.map((v) => [String(v.id), v]));
    return selectedVendorIds
      .map((id) => map.get(String(id)))
      .filter(Boolean);
  }, [selectedVendorIds, vendorOptions]);

  const toggleVendorSelected = (vendorId) => {
    setSelectedVendorIds((prev) => {
      const s = new Set((prev || []).map((x) => String(x)));
      const key = String(vendorId);
      if (s.has(key)) s.delete(key);
      else s.add(key);
      return Array.from(s);
    });
  };

  const startEdit = (loc) => {
    setEditingId(loc.id);
    setExpandedIds((prev) => new Set(prev).add(loc.id));
    setEditForm({
      location_address: {
        address: loc.location_address?.address || '',
        city: loc.location_address?.city || '',
        state: loc.location_address?.state || '',
        pincode: loc.location_address?.pincode || '',
        latitude: loc.location_address?.latitude ?? null,
        longitude: loc.location_address?.longitude ?? null,
      },
      operating_hours: loc.operating_hours || DEFAULT_HOURS,
      vendor_location_type: loc.vendor_location_type || 'VENDOR_ONLY',
      manager_name: loc.manager?.name || '',
      manager_mobile: loc.manager?.mobile_number || '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(null);
  };

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const saveEdit = async (loc) => {
    if (!editForm) return;
    setSaving(true);
    setError('');
    try {
      const patch = {};

      // Operating hours
      patch.operating_hours = editForm.operating_hours || DEFAULT_HOURS;

      // Address (include full block)
      patch.location_address = editForm.location_address;

      // Type
      if (editForm.vendor_location_type) patch.vendor_location_type = editForm.vendor_location_type;

      // Active toggle is handled by separate UI switch
      await vendorLocationsAdminService.updateVendorLocation(loc.id, patch);
      cancelEdit();
      await fetchLocations();
    } catch (e) {
      setError(e?.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (loc) => {
    setSaving(true);
    setError('');
    try {
      await vendorLocationsAdminService.updateVendorLocation(loc.id, { is_active: !loc.is_active });
      await fetchLocations();
    } catch (e) {
      setError(e?.message || 'Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const handleHoursChange = (day, field, value) => {
    setEditForm((prev) => ({
      ...prev,
      operating_hours: {
        ...(prev?.operating_hours || {}),
        [day]: {
          ...(prev?.operating_hours?.[day] || {}),
          [field]: value,
        },
      },
    }));
  };

  const handleAddressChange = (value) => {
    setEditForm((prev) => ({
      ...prev,
      location_address: { ...(prev?.location_address || {}), address: value },
    }));
  };

  const handleAddressSelect = (data) => {
    setEditForm((prev) => ({
      ...prev,
      location_address: {
        address: data.address,
        state: data.state,
        city: data.city,
        latitude: data.latitude,
        longitude: data.longitude,
        pincode: data.pincode,
      },
    }));
  };

  return (
    <div className="page-container">
      <div className="content-container">
        <div className="card mb-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-gray-900">Vendor Locations</h1>
              <p className="text-gray-600 mt-1">Select one or more vendors to view locations.</p>

              <div className="mt-4 flex items-center gap-3 flex-wrap">
                <div className="relative" ref={vendorDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setVendorDropdownOpen((v) => !v)}
                    className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white hover:bg-gray-50 min-w-[420px] max-w-[680px] flex items-center justify-between gap-3"
                  >
                    <span className="truncate">
                      {selectedVendorSummaries.length === 0
                        ? 'Select vendor(s)…'
                        : selectedVendorSummaries.length === 1
                          ? selectedVendorSummaries[0].label
                          : `${selectedVendorSummaries.length} vendors selected`}
                    </span>
                    <span className="text-gray-400">{vendorDropdownOpen ? '▴' : '▾'}</span>
                  </button>

                  {vendorDropdownOpen ? (
                    <div className="absolute left-0 mt-2 w-[560px] bg-white border border-gray-200 rounded-md shadow-lg z-50">
                      <div className="p-3 border-b border-gray-100 flex items-center gap-2">
                        <input
                          value={vendorSearch}
                          onChange={(e) => setVendorSearch(e.target.value)}
                          placeholder="Search vendors…"
                          className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          type="button"
                          onClick={() => fetchVendors(vendorSearch)}
                          className="px-3 py-2 bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200 border border-gray-200"
                          disabled={loadingVendors}
                        >
                          {loadingVendors ? '…' : 'Search'}
                        </button>
                      </div>
                      <div className="max-h-[360px] overflow-auto p-2">
                        {loadingVendors ? (
                          <div className="text-sm text-gray-600 p-3">Loading vendors…</div>
                        ) : vendors.length === 0 ? (
                          <div className="text-sm text-gray-600 p-3">No vendors.</div>
                        ) : filteredVendors.length === 0 ? (
                          <div className="text-sm text-gray-600 p-3">No vendors found.</div>
                        ) : (
                          filteredVendors.map((v) => {
                            const checked = selectedVendorIds.map(String).includes(String(v.id));
                            return (
                              <label
                                key={v.id}
                                className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-gray-50 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleVendorSelected(v.id)}
                                />
                                <span className="text-sm text-gray-800">{v.label}</span>
                              </label>
                            );
                          })
                        )}
                      </div>
                      <div className="p-3 border-t border-gray-100 flex items-center justify-between">
                        <button
                          type="button"
                          className="text-sm text-gray-600 hover:text-gray-900"
                          onClick={() => setSelectedVendorIds([])}
                        >
                          Clear selection
                        </button>
                        <button
                          type="button"
                          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                          onClick={() => {
                            setVendorDropdownOpen(false);
                            fetchLocations();
                          }}
                          disabled={selectedVendorIds.length === 0}
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>

                <button
                  onClick={fetchLocations}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  disabled={selectedVendorIds.length === 0 || loadingLocations}
                  type="button"
                >
                  {loadingLocations ? 'Refreshing…' : 'Refresh'}
                </button>
              </div>
            </div>

            <div />
          </div>
        </div>

        {error ? (
          <div className="card mb-6 bg-red-50 border-red-200">
            <div className="text-red-700">{error}</div>
          </div>
        ) : null}

        {selectedVendorIds.length === 0 ? (
          <div className="card">
            <div className="text-gray-700">Select one or more vendors to view locations.</div>
          </div>
        ) : loadingLocations ? (
          <LoadingSpinner />
        ) : locationsByVendor.length === 0 ? (
          <div className="card">
            <div className="text-gray-700">No locations found for the selected vendor(s).</div>
          </div>
        ) : (
          <div className="space-y-4">
            {locationsByVendor.map((group) => (
              <div key={group.vendor.id} className="space-y-3">
                <div className="px-1">
                  <div className="text-sm font-semibold text-gray-900">
                    {group.vendor.vendor_name}{' '}
                    <span className="text-gray-500 font-normal">({group.vendor.vendor_code})</span>
                  </div>
                  <div className="text-xs text-gray-500">{group.total} locations</div>
                </div>

                {group.locations.map((loc) => {
                  const isEditing = editingId === loc.id;
                  const isExpanded = expandedIds.has(loc.id);
                  return (
                    <div
                      key={loc.id}
                      className={`card p-0 overflow-hidden border ${
                        isExpanded ? 'border-blue-200' : 'border-gray-200'
                      }`}
                    >
                      <button
                        onClick={() => toggleExpanded(loc.id)}
                        className="w-full text-left px-5 py-4 hover:bg-gray-50 flex items-start justify-between gap-4"
                        type="button"
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="font-semibold text-gray-900 truncate">
                              {loc.location_address?.address || '—'}
                            </div>
                            <span
                              className={`text-xs px-2 py-1 rounded-full border ${
                                loc.is_active
                                  ? 'bg-green-50 text-green-700 border-green-200'
                                  : 'bg-gray-50 text-gray-600 border-gray-200'
                              }`}
                            >
                              {loc.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            {loc.location_address?.city || '—'}, {loc.location_address?.state || '—'}{' '}
                            {loc.location_address?.pincode ? `(${loc.location_address.pincode})` : ''}
                          </div>
                          <div className="text-xs text-gray-500 mt-2 flex items-center gap-2 flex-wrap">
                            <span className="bg-blue-50 text-blue-700 border border-blue-100 px-2 py-0.5 rounded-full">
                              {loc.vendor_location_type}
                            </span>
                            <span className="bg-gray-50 text-gray-700 border border-gray-200 px-2 py-0.5 rounded-full">
                              Drivers: {loc.driver_count ?? 0}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              toggleActive(loc);
                            }}
                            className={`px-3 py-1.5 rounded-md text-sm border ${
                              loc.is_active
                                ? 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
                                : 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
                            }`}
                            disabled={saving}
                            type="button"
                          >
                            {loc.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              startEdit(loc);
                            }}
                            className="px-3 py-1.5 rounded-md text-sm bg-white text-gray-800 border border-gray-200 hover:bg-gray-50"
                            disabled={saving}
                            type="button"
                          >
                            {isEditing ? 'Editing…' : 'Edit'}
                          </button>

                          <span className="text-gray-400 text-xl leading-none select-none">
                            {isExpanded ? '▾' : '▸'}
                          </span>
                        </div>
                      </button>

                      {isExpanded ? (
                        <div className="border-t border-gray-200 bg-gray-50 px-5 py-5 space-y-5">
                          {isEditing && editForm ? (
                            <div className="bg-white p-4 rounded-md border border-gray-200">
                              <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                                <h4 className="font-medium text-gray-900">Edit Location</h4>
                                <div className="text-xs text-gray-500">
                                  Location ID: <span className="font-mono">{loc.id}</span>
                                </div>
                              </div>

                              <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Address
                                </label>
                                <AddressAutocomplete
                                  value={editForm.location_address.address}
                                  onChange={handleAddressChange}
                                  onAddressSelect={handleAddressSelect}
                                  placeholder="Enter address"
                                />
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    City
                                  </label>
                                  <input
                                    value={editForm.location_address.city}
                                    onChange={(e) =>
                                      setEditForm((p) => ({
                                        ...p,
                                        location_address: { ...p.location_address, city: e.target.value },
                                      }))
                                    }
                                    className="w-full p-2 border border-gray-300 rounded-md"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    State
                                  </label>
                                  <input
                                    value={editForm.location_address.state}
                                    onChange={(e) =>
                                      setEditForm((p) => ({
                                        ...p,
                                        location_address: { ...p.location_address, state: e.target.value },
                                      }))
                                    }
                                    className="w-full p-2 border border-gray-300 rounded-md"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Pincode
                                  </label>
                                  <input
                                    value={editForm.location_address.pincode}
                                    onChange={(e) =>
                                      setEditForm((p) => ({
                                        ...p,
                                        location_address: {
                                          ...p.location_address,
                                          pincode: e.target.value,
                                        },
                                      }))
                                    }
                                    className="w-full p-2 border border-gray-300 rounded-md"
                                  />
                                </div>
                              </div>

                              <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Location Type
                                </label>
                                <select
                                  value={editForm.vendor_location_type}
                                  onChange={(e) =>
                                    setEditForm((p) => ({ ...p, vendor_location_type: e.target.value }))
                                  }
                                  className="w-full p-2 border border-gray-300 rounded-md"
                                >
                                  <option value="VENDOR_ONLY">Vendor Only</option>
                                  <option value="VENDOR_AND_DEALER">Vendor & Dealer</option>
                                  <option value="DEALER_PARTNER">Dealer Partner</option>
                                </select>
                              </div>

                              <div>
                                <h5 className="font-medium text-gray-900 mb-2">Operating Hours</h5>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  {Object.entries(editForm.operating_hours || DEFAULT_HOURS).map(
                                    ([day, hours]) => (
                                      <div key={day} className="bg-white p-3 rounded-md border border-gray-200">
                                        <div className="text-sm font-medium text-gray-700 capitalize mb-2">
                                          {day}
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                          <div>
                                            <label className="block text-xs text-gray-600 mb-1">
                                              Open
                                            </label>
                                            <input
                                              type="time"
                                              value={hours.open}
                                              onChange={(e) => handleHoursChange(day, 'open', e.target.value)}
                                              className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                            />
                                          </div>
                                          <div>
                                            <label className="block text-xs text-gray-600 mb-1">
                                              Close
                                            </label>
                                            <input
                                              type="time"
                                              value={hours.close}
                                              onChange={(e) => handleHoursChange(day, 'close', e.target.value)}
                                              className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    ),
                                  )}
                                </div>
                              </div>

                              <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
                                <button
                                  onClick={cancelEdit}
                                  className="px-4 py-2 bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200 border border-gray-200"
                                  disabled={saving}
                                  type="button"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={() => saveEdit(loc)}
                                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                                  disabled={saving}
                                  type="button"
                                >
                                  {saving ? 'Saving…' : 'Save Changes'}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="text-sm text-gray-600">
                              Click <span className="font-medium">Edit</span> to modify address, type, or operating hours.
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

